import * as rentRegistrationActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.actions";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationBrusselsStatus,
  ERegistrationDocumentStatus,
  ERegistrationDocumentType,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { hasBrusselsRegistration } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { stringToSnakeCase } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import { renderTooltipChecklist } from "../../../../utils/tooltip.utils";
import StatusDot, { EStatus } from "../StatusDot";

interface IProps {
  property: IProperty;
  contract: IContract;
}

const StatusDotRegistration = (props: IProps) => {
  const dispatch = useDispatch();

  const { property, contract } = props;

  const registration = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.getDetail(state, contract?.rentRegistrationId),
  );

  const isFetchingRegistration = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.isFetchingDetail(
      state,
      contract?.rentRegistrationId,
    ),
  );

  React.useEffect(() => {
    if (isFetchingRegistration || !contract?.rentRegistrationId) {
      return;
    }

    dispatch(
      rentRegistrationActions.getDetailStart.getAction({
        id: contract?.rentRegistrationId,
      }),
    );
  }, [contract?.rentRegistrationId]);

  // Helpers
  const getStatus = () => {
    const { manuallyCompletedRegistration, rentRegistrationBrusselsStatus } =
      contract;

    if (isFetchingRegistration) {
      return EStatus.None;
    }
    if (manuallyCompletedRegistration) {
      return EStatus.Success;
    }

    const rentRegistrationDocuments = registration?.documents;

    const validForBrusselsRegistration = hasBrusselsRegistration(
      property,
      contract.leaseType,
    );

    const contractDocument = rentRegistrationDocuments?.find(
      document => document?.documentType === ERegistrationDocumentType.Contract,
    );

    const propertyInspectionDocument = rentRegistrationDocuments?.find(
      document =>
        document?.documentType ===
        ERegistrationDocumentType.PropertyInspectionReport,
    );

    const addendumDocument = rentRegistrationDocuments?.find(
      document => document?.documentType === ERegistrationDocumentType.Addendum,
    );

    const contractDocumentStatus =
      contractDocument?.status || ERegistrationDocumentStatus.None;
    const propertyInspectionDocumentStatus =
      propertyInspectionDocument?.status || ERegistrationDocumentStatus.None;

    const documentStatuses = [
      contractDocumentStatus,
      propertyInspectionDocumentStatus,
    ];

    if (validForBrusselsRegistration && rentRegistrationBrusselsStatus) {
      // @ts-ignore
      documentStatuses.push(rentRegistrationBrusselsStatus);
    }

    if (addendumDocument) {
      documentStatuses.push(addendumDocument?.status);
    }

    if (manuallyCompletedRegistration) {
      return EStatus.Success;
    }

    if (
      documentStatuses.every(
        status =>
          status &&
          [
            ERegistrationDocumentStatus.Registered,
            ERegistrationDocumentStatus.NotApplicable,
            ERegistrationDocumentStatus.RegisteredManually,
          ].includes(status),
      )
    ) {
      return EStatus.Success;
    }

    return EStatus.Error;
  };

  // Render
  const renderTooltipContent = () => {
    const { manuallyCompletedRegistration, rentRegistrationBrusselsStatus } =
      contract;

    if (isFetchingRegistration) {
      return;
    }

    if (manuallyCompletedRegistration) {
      return getLocalizedText(manuallyCompletedRegistration);
    }

    const rentRegistrationDocuments = registration?.documents;

    const contractDocument = rentRegistrationDocuments?.find(
      document => document?.documentType === ERegistrationDocumentType.Contract,
    );

    const propertyInspectionDocument = rentRegistrationDocuments?.find(
      document =>
        document?.documentType ===
        ERegistrationDocumentType.PropertyInspectionReport,
    );

    const addendumDocument = rentRegistrationDocuments?.find(
      document => document?.documentType === ERegistrationDocumentType.Addendum,
    );

    const rentRegistrationRentContractStatus =
      contractDocument?.status || ERegistrationDocumentStatus.None;
    const rentRegistrationLocationDescriptionStatus =
      propertyInspectionDocument?.status || ERegistrationDocumentStatus.None;
    const rentRegistrationAddendumStatus = addendumDocument?.status;

    const checklist = [];

    checklist.push({
      label: getLocalizedText("registration.status_info.contract", {
        status: getLocalizedText(
          `registration.status.${stringToSnakeCase(
            rentRegistrationRentContractStatus,
          )}`.toLowerCase(),
        ),
      }),
      success: [
        ERegistrationDocumentStatus.Registered,
        ERegistrationDocumentStatus.RegisteredManually,
        ERegistrationDocumentStatus.NotApplicable,
      ].includes(rentRegistrationRentContractStatus),
    });

    checklist.push({
      label: getLocalizedText("registration.status_info.location_description", {
        status: getLocalizedText(
          `registration.status.${stringToSnakeCase(
            rentRegistrationLocationDescriptionStatus,
          )}`.toLowerCase(),
        ),
      }),
      success: [
        ERegistrationDocumentStatus.Registered,
        ERegistrationDocumentStatus.RegisteredManually,
        ERegistrationDocumentStatus.NotApplicable,
      ].includes(rentRegistrationLocationDescriptionStatus),
    });

    if (
      hasBrusselsRegistration(property, contract.leaseType) &&
      rentRegistrationBrusselsStatus
    ) {
      checklist.push({
        label: getLocalizedText("registration.status_info.brussels", {
          status: getLocalizedText(
            `registration.status.${stringToSnakeCase(
              rentRegistrationBrusselsStatus,
            )}`.toLowerCase(),
          ),
        }),
        success: [
          ERegistrationBrusselsStatus.Registered,
          ERegistrationBrusselsStatus.RegisteredManually,
        ].includes(rentRegistrationBrusselsStatus),
      });
    }

    if (rentRegistrationAddendumStatus) {
      checklist.push({
        label: getLocalizedText("registration.status_info.addendum", {
          status: getLocalizedText(
            `registration.status.${stringToSnakeCase(
              rentRegistrationAddendumStatus,
            )}`.toLowerCase(),
          ),
        }),
        success: [
          ERegistrationDocumentStatus.Registered,
          ERegistrationDocumentStatus.RegisteredManually,
          ERegistrationDocumentStatus.NotApplicable,
        ].includes(rentRegistrationAddendumStatus),
      });
    }

    return renderTooltipChecklist(checklist);
  };

  return (
    <StatusDot status={getStatus()} tooltipContent={renderTooltipContent()} />
  );
};

// eslint-disable-next-line import/no-default-export
export default StatusDotRegistration;
